import { type FC } from "react";

import { Input } from "@/shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { FieldError } from "@/shared/components/ui/FieldError";
import { RELATIONSHIP_OPTIONS } from "@/shared/constants/relationshipOptions";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import { formatPhoneInput } from "@/shared/utils/formatters";
import {
  Tooltip,
  TooltipContent,
  TooltipInfoTrigger,
  TooltipProvider,
} from "@/shared/components/ui/tooltip";
import { WHY_WE_ASK, WHY_WE_ASK_TITLE } from "@/shared/constants/whyWeAsk";

export function createAboutYourself(decedentName: string): FC<FlowCustomComponentProps> {
  const AboutYourself: FC<FlowCustomComponentProps> = ({ answers, setAnswer, errors }) => {
    const fullName = (answers["invitee-full-name"] as string) ?? "";
    const phone = (answers["invitee-phone"] as string) ?? "";
    const relationship = (answers["invitee-relationship"] as string) ?? "";

    const nameError = errors?.["invitee-full-name"];
    const phoneError = errors?.["invitee-phone"];
    const relationshipError = errors?.["invitee-relationship"];

    return (
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="invitee-full-name" className="text-sm font-medium text-primary">
            Full Name
          </label>
          <Input
            id="invitee-full-name"
            type="text"
            placeholder="John Doe"
            value={fullName}
            onChange={(e) => setAnswer("invitee-full-name", e.target.value)}
            className={nameError ? "border-destructive" : undefined}
            style={nameError ? { borderWidth: "2px" } : undefined}
          />
          {nameError && <FieldError message={nameError} />}
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="invitee-phone" className="text-sm font-medium text-primary">
            Phone Number
            <TooltipProvider>
              <Tooltip>
                <TooltipInfoTrigger aria-label={WHY_WE_ASK_TITLE} className="ml-1 align-middle" />
                <TooltipContent title={WHY_WE_ASK_TITLE}>{WHY_WE_ASK.userPhone}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </label>
          <Input
            id="invitee-phone"
            type="text"
            placeholder="(123) 123-1234"
            value={phone}
            onChange={(e) => setAnswer("invitee-phone", formatPhoneInput(e.target.value))}
            className={phoneError ? "border-destructive" : undefined}
            style={phoneError ? { borderWidth: "2px" } : undefined}
          />
          {phoneError && <FieldError message={phoneError} />}
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-primary">I am {decedentName}'s...</label>
          <Select
            value={relationship}
            onValueChange={(value) => setAnswer("invitee-relationship", value)}
          >
            <SelectTrigger
              className={relationshipError ? "border-destructive" : undefined}
              style={relationshipError ? { borderWidth: "2px" } : undefined}
            >
              <SelectValue placeholder="Select a relationship" />
            </SelectTrigger>
            <SelectContent>
              {RELATIONSHIP_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {relationshipError && <FieldError message={relationshipError} />}
        </div>
      </div>
    );
  };

  return AboutYourself;
}
